/**
 * WorkflowStepInspections (Step 6)
 *
 * Inspection requirements and preparation.
 * Substeps: requiredInspections, checkpoints, knownTips, acknowledgment
 *
 * Part of Phase 8: Job Workflow Wizard
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  ClipboardCheck,
  Lightbulb,
  AlertTriangle,
  Check,
  Camera,
  FileText,
  Tag,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { UseJobWorkflowReturn } from '@/hooks/useJobWorkflow';
import type { JobData } from '../WorkflowContainer';

interface WorkflowStepInspectionsProps {
  jobId: string;
  jobData: JobData;
  workflow: UseJobWorkflowReturn;
}

interface InspectionType {
  id: string;
  name: string;
  phase: 'rough' | 'pre-cover' | 'final';
  description: string;
  checkpoints: string[];
  tips?: string[];
}

// Mock inspection types based on trade
const getInspectionsForTrade = (trade: string): InspectionType[] => {
  switch (trade) {
    case 'electrical':
      return [
        {
          id: 'rough_elec',
          name: 'Rough Electrical',
          phase: 'rough',
          description: 'Wiring, boxes, and conduit before covering',
          checkpoints: [
            'All boxes securely fastened',
            'Proper wire sizing for circuits',
            'Grounding connections complete',
            'AFCI/GFCI protection installed',
          ],
          tips: [
            'Have wire fill calculations available',
            'Ensure all knockouts are covered',
          ],
        },
        {
          id: 'final_elec',
          name: 'Final Electrical',
          phase: 'final',
          description: 'Complete installation with fixtures and covers',
          checkpoints: [
            'All devices and covers installed',
            'Panel directory complete',
            'GFCI outlets test properly',
            'Smoke detectors interconnected',
          ],
          tips: ['Test all GFCI outlets before inspector arrives'],
        },
      ];
    case 'mechanical':
      return [
        {
          id: 'rough_mech',
          name: 'Rough Mechanical',
          phase: 'rough',
          description: 'Ductwork and refrigerant lines before covering',
          checkpoints: [
            'Ductwork properly supported',
            'Refrigerant lines insulated',
            'Drain lines properly sloped',
          ],
        },
        {
          id: 'final_mech',
          name: 'Final Mechanical',
          phase: 'final',
          description: 'Complete system startup and testing',
          checkpoints: [
            'Equipment labels visible',
            'Disconnect within sight',
            'Condensate properly draining',
            'System operational',
          ],
        },
      ];
    default:
      return [
        {
          id: 'rough',
          name: 'Rough Inspection',
          phase: 'rough',
          description: 'Work before covering',
          checkpoints: ['Work matches approved plans', 'Code compliance verified'],
        },
        {
          id: 'final',
          name: 'Final Inspection',
          phase: 'final',
          description: 'Complete installation',
          checkpoints: ['All work complete', 'Ready for use'],
        },
      ];
  }
};

export function WorkflowStepInspections({ jobId, jobData, workflow }: WorkflowStepInspectionsProps) {
  const currentSubstep = workflow.currentSubstepId;
  const [acknowledged, setAcknowledged] = useState(false);
  const [expandedInspection, setExpandedInspection] = useState<string | null>(null);

  const inspections = getInspectionsForTrade(jobData.trade || jobData.permitType || 'general');

  // Mark substeps complete
  useEffect(() => {
    workflow.markSubstepComplete('inspections', 'requiredInspections');
    if (acknowledged) {
      workflow.markSubstepComplete('inspections', 'acknowledgment');
    }
  }, [acknowledged, workflow]);

  const phaseColors = {
    rough: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800',
    'pre-cover': 'bg-muted/50 text-foreground border-border dark:bg-muted/30 dark:text-foreground dark:border-border',
    final: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-200 dark:border-green-800',
  };

  return (
    <div className="space-y-6">
      {/* Inspection Sequence */}
      <Card className={cn(currentSubstep === 'requiredInspections' && 'ring-2 ring-primary')}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5" />
            Required Inspections
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground mb-4">
              These inspections are required for your {jobData.trade || jobData.permitType} permit.
              Each must pass before proceeding to the next phase.
            </p>

            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 top-8 bottom-8 w-0.5 bg-muted" />

              {inspections.map((inspection, index) => (
                <div key={inspection.id} className="relative flex gap-4 pb-6 last:pb-0">
                  {/* Timeline dot */}
                  <div
                    className={cn(
                      'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 bg-background z-10',
                      phaseColors[inspection.phase]
                    )}
                  >
                    {index + 1}
                  </div>

                  {/* Content */}
                  <div className="flex-1 pt-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium">{inspection.name}</h4>
                      <Badge variant="outline" className={cn('text-xs', phaseColors[inspection.phase])}>
                        {inspection.phase}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {inspection.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Checkpoint Checklists */}
      <Card className={cn(currentSubstep === 'checkpoints' && 'ring-2 ring-primary')}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Check className="h-5 w-5" />
            Pre-Inspection Checklists
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion
            type="single"
            collapsible
            value={expandedInspection || undefined}
            onValueChange={(v) => setExpandedInspection(v)}
          >
            {inspections.map((inspection) => (
              <AccordionItem key={inspection.id} value={inspection.id}>
                <AccordionTrigger className="hover:no-underline">
                  <span className="font-medium">{inspection.name} Checkpoints</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-2">
                    {/* Checkpoints */}
                    <div className="space-y-2">
                      {inspection.checkpoints.map((checkpoint, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <Checkbox id={`${inspection.id}-${idx}`} className="mt-0.5" />
                          <Label
                            htmlFor={`${inspection.id}-${idx}`}
                            className="text-sm cursor-pointer"
                          >
                            {checkpoint}
                          </Label>
                        </div>
                      ))}
                    </div>

                    {/* Tips */}
                    {inspection.tips && inspection.tips.length > 0 && (
                      <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 dark:bg-amber-950/30 dark:border-amber-800">
                        <div className="flex items-center gap-2 mb-2">
                          <Lightbulb className="h-4 w-4 text-amber-500" />
                          <span className="text-sm font-medium text-amber-700 dark:text-amber-300">Tips</span>
                        </div>
                        <ul className="space-y-1">
                          {inspection.tips.map((tip, idx) => (
                            <li key={idx} className="text-sm text-amber-700 dark:text-amber-300 flex gap-2">
                              <span className="text-amber-400">•</span>
                              {tip}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Pre-inspection reminders */}
                    <div className="flex gap-2 pt-2">
                      <Badge variant="outline" className="text-xs">
                        <Camera className="h-3 w-3 mr-1" />
                        Take photos
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        <FileText className="h-3 w-3 mr-1" />
                        Have plans on site
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        <Tag className="h-3 w-3 mr-1" />
                        Verify labels
                      </Badge>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>

      {/* Acknowledgment */}
      <Card className={cn(currentSubstep === 'acknowledgment' && 'ring-2 ring-primary')}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Important Reminders
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <span className="text-amber-500">!</span>
                Never cover work before the required inspection passes
              </li>
              <li className="flex gap-2">
                <span className="text-amber-500">!</span>
                Have the permit card and approved plans posted on site
              </li>
              <li className="flex gap-2">
                <span className="text-amber-500">!</span>
                Take photos before, during, and after each inspection
              </li>
              <li className="flex gap-2">
                <span className="text-amber-500">!</span>
                Schedule inspections at least 24 hours in advance
              </li>
            </ul>

            <div className="pt-4 border-t">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="acknowledge"
                  checked={acknowledged}
                  onCheckedChange={(checked) => setAcknowledged(checked === true)}
                  className="mt-0.5"
                />
                <div>
                  <Label htmlFor="acknowledge" className="font-medium cursor-pointer">
                    I understand the inspection requirements
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    I will ensure all checkpoints are verified before scheduling each inspection.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
